import { useContext, useRef } from 'react';

import {
  DREAM_COMPONENTS_CONTEXT_DEFAULT_VALUES,
  DreamComponentsContext,
  type DreamComponentsContextType,
  HighPriorityIndexContext,
  ImageIndexContext,
} from './context';

export const useDreamComponentsContext = (): DreamComponentsContextType => {
  const context = useContext(DreamComponentsContext) ?? DREAM_COMPONENTS_CONTEXT_DEFAULT_VALUES;
  return context;
};

export interface UseImagePriorityOptions {
  hasImage?: boolean;
  highPriorityEligible?: boolean;
}

export const useImagePriority = (
  optionsOrHasImage: UseImagePriorityOptions | boolean = true,
): {
  fetchPriority?: 'high';
  loading?: 'eager' | 'lazy';
  isHighPriority?: boolean;
} => {
  const options: UseImagePriorityOptions =
    typeof optionsOrHasImage === 'boolean' ? { hasImage: optionsOrHasImage } : optionsOrHasImage;
  const { hasImage = true, highPriorityEligible = false } = options;

  const { imagePriorityThreshold, imageHighPriorityCount } = useDreamComponentsContext();
  const getNextIndex = useContext(ImageIndexContext);
  const getNextHighPriorityIndex = useContext(HighPriorityIndexContext);
  const indexRef = useRef<number | null>(null);
  const highPriorityIndexRef = useRef<number | null>(null);

  // Only increment the counter once per component mount.
  // Using a guard instead of useRef(getNextIndex()) to avoid
  // double-incrementing in React strict mode's double-render.
  // Skip incrementing when there's no image to avoid wasting a slot.
  if (indexRef.current === null && getNextIndex && hasImage) {
    indexRef.current = getNextIndex();
  }

  // Claim a high-priority slot if eligible and the counter is available
  if (highPriorityIndexRef.current === null && getNextHighPriorityIndex && hasImage && highPriorityEligible) {
    highPriorityIndexRef.current = getNextHighPriorityIndex();
  }

  if (!imagePriorityThreshold || indexRef.current == null) {
    return {};
  }

  const isEager = indexRef.current <= imagePriorityThreshold;

  // Determine if this image gets fetchPriority="high"
  // If imageHighPriorityCount is set, only eager eligible images within that budget get high priority.
  // Otherwise, fall back to the legacy behavior where isEager implies high priority.
  let isHighPriority = false;
  if (imageHighPriorityCount != null) {
    isHighPriority =
      isEager &&
      highPriorityEligible &&
      highPriorityIndexRef.current != null &&
      highPriorityIndexRef.current <= imageHighPriorityCount;
  } else {
    isHighPriority = isEager;
  }

  return {
    fetchPriority: isHighPriority ? 'high' : undefined,
    loading: isEager ? 'eager' : 'lazy',
    isHighPriority,
  };
};
